import axios from 'axios'
import { uniq, uniqBy } from 'lodash'
import { enrollingForFirstTime } from '../helpers/enrolment'
import AddressType from '../enums/AddressType'
import { getCohortCodes, sortYearAsc } from '../helpers/enrolment'
import ProcessType from '../enums/ProcessType'
import { getConfiguration } from '../helpers/configuration'
import { isAfter } from 'date-fns'
import { getDifferenceInMinutes } from '../actions/dateUtility';
import { COURSE_SUMMARY_SESSION_STORAGE_KEY } from '../helpers/course'

axios.interceptors.request.use(
  function(config) {
    if (!(config.url.startsWith('https') || config.url.startsWith('http'))) {
      let headers = config.headers

      let tokenType = 'Bearer'
      let token = sessionStorage.getItem('JW_TOKEN')

      if (config.url === '/api/jwt/token') {
        tokenType = 'Basic'

        token = sessionStorage.getItem('token')
        if (!token) {
          token = btoa(getConfiguration('jwtUsername') + ':' + getConfiguration('jwtPassword'))
          sessionStorage.setItem('token', token)
        }
      }

      if (token !== null && typeof token !== 'undefined') {
        Object.assign(headers, {
          Authorization: tokenType + ' ' + token
        })

        config.headers = headers
      }
    }

    return config
  },
  function(error) {
    // Do something with request error
    return Promise.reject(error)
  }
)

export const get = (url, options = {}) => {
  return axios
    .get(url, options)
    .then(response => response.data)
    .catch(handleErrorResponse)
}

export const post = (url, params) => {
  return axios
    .post(url, params)
    .then(response => response.data)
    .catch(handleErrorResponse)
}

export const postAsForm = (url, params) => {
  return axios
    .post(url, null, params, {
      headers: {
        'X-Request-With': 'XMLHttpRequest'
      }
    })
    .then(response => response.data)
    .catch(handleErrorResponse)
}

export const put = (url, params) => {
  return axios
    .put(url, params)
    .then(response => response.data)
    .catch(handleErrorResponse)
}

export const axiosDelete = (url, options = {}) => {
  return axios
    .delete(url, options)
    .then(response => response.data)
    .catch(handleErrorResponse)
}

export const handleErrorResponse = error => {
  if (error.response && error.response.status === 403) {
    localStorage.clear()
    sessionStorage.clear()

    window.location.reload()
  } else if (error.message === 'Network Error') {
    window.location.reload()
  } else {
    throw error
  }
}

export const getJwtAuthenticationToken = () => {
  return axios
    .get('/api/jwt/token')
    .then(response => {
      return response.data
    })
    .then(data => {
      if (data.token === null || data.token === undefined || data.token === '' || data.token === '') {
        return [false, true]
      }
      sessionStorage.setItem('JW_TOKEN', data.token)
      return [true, false]
    })
    .catch(error => {
      return [false, true]
    })
}

export const getReservoirAuthToken = () => {
  return get('/api/reservoir/getAuthToken').then(data => {
    sessionStorage.setItem('cms_url', `${data.protocol}://${data.host}`)
    sessionStorage.setItem('access_token', JSON.stringify(data.token))

    return data
  })
}

export const getReservoirApiUrl = urlPath => {
  return `${sessionStorage.getItem('cms_url')}/jsonapi/${urlPath}`
}

export const getAccessToken = () => {
  if (sessionStorage.getItem('access_token')) {
    let token = JSON.parse(sessionStorage.getItem('access_token'))
    if (isAfter(new Date(token.expiresAtString), new Date())) {
      return new Promise(resolve => {
        resolve(token.tokenValue)
      })
    }
  }

  return getReservoirAuthToken().then(authToken => authToken.token.tokenValue)
}

const freshGet = (cacheKey, getRequest, params = {}) => {
  return get(getRequest, params).then(response => {
    sessionStorage.setItem(cacheKey, JSON.stringify(response))

    return response
  })
}

/**
 *
 * @param {string} cacheKey Unique identifier for the cached resource
 * @param {string} getRequest The URL where the resource is stored
 * @param {object} params
 * @returns {Promise}
 */
const cacheGet = (cacheKey, getRequest, params = {}) => {
  if (!sessionStorage.getItem(cacheKey)) {
    return get(getRequest, params).then(response => {
      sessionStorage.setItem(cacheKey, JSON.stringify(response))

      return response
    })
  }

  try {
    const payload = JSON.parse(sessionStorage.getItem(cacheKey))
    return new Promise(resolve => {
      resolve(payload)
    })
  } catch (error) {
    return freshGet(cacheKey, getRequest, params)
  }
}

export const getVisaClassDetails = () => {
  return cacheGet('VISA_TYPE', 'api/systemData/sone/visaClass')
}

export const getVisaSubclassDetails = () => {
  return cacheGet('VISA_SUB_TYPE', 'api/systemData/sone/visaSubclass')
}

export const getCoursesSummary = (qParams = {}) => {
  let coursesSummary = JSON.parse(sessionStorage.getItem(COURSE_SUMMARY_SESSION_STORAGE_KEY))
  if (coursesSummary && lessThanThirty(coursesSummary.date)) {
    return new Promise(resolve => {
      resolve(coursesSummary.response)
    })
  }

  return get('/api/courses/summary', {params: qParams}).then(response => {
    sessionStorage.setItem('COURSES_SUMMARY', JSON.stringify({ response: response, date: new Date() }))

    return response
  })
}

export const getCourseDetails = (courseId, qParams = {}) => {
  return get('/api/courses/' + courseId, {params: qParams})
}

export const getEnrolmentReadiness = sspNo => {
  let queryParams = {
    sspNo: sspNo
  }

  return get('/api/student/enrolmentReadiness', { params: queryParams })
}

export const getOfferDetails = (applicationId, applicationLineId, courseCode) => {
  let queryParams = {}

  if (applicationId) {
    queryParams['applicationId'] = applicationId
  }

  if (applicationLineId) {
    queryParams['applicationLineId'] = applicationLineId
  }

  if (courseCode) {
    queryParams['courseCode'] = courseCode
  }

  return get('/api/offer/details', {
    params: queryParams
  })
}

export const putOfferAcceptance = params => {
  return put('/api/offer/acceptance', params)
}

export const putGovernmentStatistics = params => {
  return put('/api/student/governmentStatistics', params)
}

export const getGovernmentStatistics = () => {
  return get('/api/student/governmentStatistics')
}

export const getSoneDeclaration = studyPackageNo => {
  return get('/api/student/studentDeclaration?studyPackageNo=' + studyPackageNo, {})
}

export const getContentOfferPageAccept = expectedStartDate => {
  return cacheGet('CONTENT_OFFER_RESPONSES', '/api/offer/page/accept?expectedStartDate=' + expectedStartDate)
}

export const updateSoneDeclaration = payload => put('/api/student/studentDeclaration', payload)

export const getSonePersonalDetails = () => {
  return cacheGet('SONE_PERSONAL_DETAILS', '/api/student/personalData')
}

export const getChoiceLinesParent = (parentSSPNumber, academicYear) => {
  return get('/api/choiceLines/parent?parentSspNo=' + parentSSPNumber + '&academicYear=' + academicYear)
}

export const getChoiceLines = choiceLine => {
  return get(
    '/api/choiceLines/?structureComponentID=' +
      choiceLine.structureComponentId +
      '&parentStudentStudyPackage=' +
      choiceLine.parentStudentStudyPackage +
      '&parentSspNo=' +
      choiceLine.parentStudentStudyPackage
  )
}

export const putChoiceLines = params => {
  return put('/api/choiceLines/', params)
}

export const deleteChoiceLines = queryParams => {
  return axiosDelete('/api/choiceLines/', {
    params: queryParams
  })
}

export const putSonePersonalDetails = params => {
  return put('/api/student/personalData', params).then(() => {
    // Refresh the personal details cache
    sessionStorage.removeItem('SONE_PERSONAL_DETAILS')

    return getSonePersonalDetails()
  })
}

// export const getSoneOfferByCourseId = courseId => {
//   return get('/api/student/courseDetails?courseId=' + courseId, {})
// }

export const postCoursesFundingFilter = (fundingTypes, liabilityCode, stuCitizenCode) => {
  let params = {}

  if (fundingTypes) {
    params = {
      fundingTypes: fundingTypes
    }
  }

  if (liabilityCode) {
    params = {
      ...params,
      liabilityCode: liabilityCode
    }
  }

  if (stuCitizenCode) {
    params = {
      ...params,
      stuCitizenCode: stuCitizenCode
    }
  }

  return post('/api/courses/funding/filtered', params)
}

export const getSoneFundingByCourseSSPNo = (courseSSPNo, liabilityCode, stuCitizenCode) => {
  let queryParams = {}

  if (courseSSPNo) {
    queryParams['sspNumber'] = courseSSPNo
  }

  if (liabilityCode) {
    queryParams['liabilityCode'] = liabilityCode
  }

  if (stuCitizenCode) {
    queryParams['stuCitizenCode'] = stuCitizenCode
  }

  return get('/api/student/fundingDetails', {
    params: queryParams
  })
}

export const getPatternOfStudy = parentSSPNumber => {
  return get('/api/student/patternOfStudy?parentSSPNumber=' + parentSSPNumber, {})
}

export const getCohorts = courseId => {
  return get('/api/student/cohorts', {
    params: {
      courseId: courseId
    }
  })
}

export const searchValidUnits = (courseStudentStudyPackageNumber = '', academicYear = '', studyPeriod = '', unitCode = '', unitTitle = '') => {
  return searchUnits(courseStudentStudyPackageNumber, academicYear, studyPeriod, unitCode, unitTitle)
}

export const searchUnits = (courseStudentStudyPackageNumber = '', academicYear = '', studyPeriod = '', unitCode = '', unitTitle = '') => {
  let cacheUnitSearch = `UNIT_SEARCH`

  let params = {}
  if (courseStudentStudyPackageNumber) {
    params['courseStudentStudyPackageNumber'] = courseStudentStudyPackageNumber
    cacheUnitSearch += `_COURSESSPO_${courseStudentStudyPackageNumber}`
  }

  if (academicYear) {
    params['academicYear'] = academicYear
    cacheUnitSearch += `_ACADEMICYEAR_${academicYear}`
  }

  if (studyPeriod) {
    params['studyPeriod'] = studyPeriod
    cacheUnitSearch += `_STUDYPERIOD_${studyPeriod}`
  }

  if (unitCode) {
    params['unitCode'] = unitCode
    cacheUnitSearch += `_UNITCODE_${unitCode}`
  }

  if (unitTitle) {
    params['unitTitle'] = unitTitle
    cacheUnitSearch += `_UNITTITLE_${unitTitle}`
  }

  return cacheGet(cacheUnitSearch, '/api/units/search', {
    params: {
      ...params
    }
  })
}

export const checkPrefillEligibilityContinuing = (courseId, enrollingAcademicYear) => {
  return getAllEnrolledUnits(courseId).then(({ unitSet }) => {
    let unitSetEnrollingYear = unitSet.filter(unit => unit.unit.supAlternateCd === enrollingAcademicYear)
    return (
      unitSetEnrollingYear.length === 0 ||
      unitSetEnrollingYear.every(({ unit }) => unit.attemptStatus === 'UNCONFIRM' || (unit.attemptStatus === null && (unit.patternstatus === 'ERROR' || unit.patternstatus === 'FULL')))
    )
  })
}

export const putStudentPatternOfStudy = (parentSSPNo, academicYear) => {
  return put('/api/student/patternOfStudy', {
    parentSSPNo: parentSSPNo,
    academicYear: academicYear
  })
}

/**
 * Only show current year units where the teaching period for that unit hasn't
 * yet expired. For example, if a unit is in SEM-1-B1 and 2017 and the
 * teaching period for that unit (found using the teaching period API) is
 * closed then we filter it out.
 *
 * We only do this check for the current year and not future years (since we
 * can logically infer that units in future years do not yet have expired
 * teaching periods).
 *
 * @param {Array} teachingCalendars
 * @param {function(expiredPeriods: Array)} callback
 * @return {function({supAlternateCd: string, calType: string})}
 */
export const filterExpiredTeachingPeriodsFromUnits = (teachingCalendars = [], callback) => {
  const expiredPeriods = teachingCalendars.map(({ acadAlternateCd, teachCalType, teachEndDt }) => ({
    year: acadAlternateCd,
    calendarType: teachCalType,
    expired: new Date() > new Date(teachEndDt)
  }))

  return callback(expiredPeriods)
}

/**
 * @param {Array} expiredPeriods
 */
const teachingPeriodFilterUnitSearchCallback = expiredPeriods => ({ supAlternateCd, calType }) => {
  if (parseInt(supAlternateCd) === new Date().getFullYear()) {
    const period = expiredPeriods.find(period => {
      return period.year === supAlternateCd && period.calendarType === calType
    })

    if (period) {
      return !period.expired
    }
  }

  return true
}

/**
 * @param {Array} expiredPeriods
 */
export const teachingPeriodFilterEnrolledUnitsCallback = expiredPeriods => ({ supAlternateCd, calendarType }) => {
  if (parseInt(supAlternateCd) === new Date().getFullYear()) {
    const period = expiredPeriods.find(period => {
      return period.year === supAlternateCd && period.calendarType === calendarType
    })

    if (period) {
      return !period.expired
    }
  }

  return true
}

export const getReferences = () => {
  return cacheGet('reference_data', '/api/reference')
}

export const getEnrolmentGuide = (courseStudentStudyPackageNumber, academicYear) =>
  get('/api/units/enrolment/guide', {
    params: {
      courseStudentStudyPackageNumber: courseStudentStudyPackageNumber,
      academicYear: academicYear
    }
  })

export const getAllEnrolledUnits = (parentSSPNo, patternId, patternStatus, includePastYears = true) =>
  get('/api/student/enrolment', {
    params: {
      parentSSPNumber: parentSSPNo,
      patternId: patternId,
      patternStatus: patternStatus,
      includePastYears: includePastYears
    }
  })

export const getCurrentEnrolledUnits = (parentSSPNo, patternId, patternStatus) => getAllEnrolledUnits(parentSSPNo, patternId, patternStatus, false)

export const getActiveEnrolledUnits = courseId =>
  getCurrentEnrolledUnits(courseId).then(units => {
    return Object.assign({}, units, {
      unitSet: units.unitSet.filter(unit => unit.unit.attemptStatus !== 'DISCONTIN')
    })
  })

export const updateEnrolment = payload => {
  return put('/api/student/enrolment', payload)
}

export const removeUnit = payload => put('/api/student/enrolmentWithdraw', payload)

export const getProperties = () => {
  return get('/api/properties/enrol')
}

export const getTeachingCalendars = (from = '', to = '') => {
  const cacheName = `CALENDAR_TEACHING_FROM_${from}_TO_${to}`
  let params = {}

  if (from) {
    params['from'] = from
  }

  if (to) {
    params['to'] = to
  }

  return cacheGet(cacheName, '/api/calendar/teaching', {
    params: params
  })
}

export const getStudyPeriodWeightings = () => {
  const cacheName = 'getStudyPeriodWeightings'

  return cacheGet(cacheName, '/api/properties/studyPeriodWeightings')
}

export const getStudyPeriodToSemesterNameCacheKey = 'getStudyPeriodToSemesterName'

export const getStudyPeriodToSemesterName = () => {
  return cacheGet(getStudyPeriodToSemesterNameCacheKey, '/api/properties/studyPeriodToSemesterName')
}

export const getLatestEnrolmentCalendar = courseId => {
  return getActiveEnrolmentCalendar(courseId).then(latestEnrolmentCalendars => {
    if (latestEnrolmentCalendars.length > 0) {
      return latestEnrolmentCalendars[latestEnrolmentCalendars.length - 1]
    }
    throw 'No available cohorts.'
  })
}

export const getLatestEnrolmentCalendarYear = courseId => {
  return getActiveEnrolmentCalendar(courseId).then(latestEnrolmentCalendars => {
    if (latestEnrolmentCalendars.length > 0) {
      const acadYear = latestEnrolmentCalendars.map(cohort => cohort.cohortDtls.acadAlternateCode).sort(sortYearAsc)[latestEnrolmentCalendars.length - 1]
      return acadYear
    }
    return null
  })
}

export const getYearsEnrolled = units => {
  return uniqBy(units, 'supAlternateCd')
    .map(unit => unit.supAlternateCd)
    .sort(sortYearAsc)
}

export const getActiveEnrolmentCalendar = courseId => {
  const cohortCd = getCohortCodes()

  return Promise.all([getActiveCalendarEnrolments(cohortCd, [ProcessType.Enrolment, ProcessType.Variation]), getStudentCalendarsByCourseId(courseId)]).then(
    ([enrolmentCalendars, studentCalendars]) => {
      const enrolmentCalendarsList = enrolmentCalendars.filter(enrolmentCalendar =>
        studentCalendars.some(studentCalendar => {
          return enrolmentCalendar.cohortDtls.ciSequenceNo === studentCalendar.scohortDtls.ciSequenceNo
        })
      )

      return enrolmentCalendarsList
    }
  )
}

export const getActiveCalendarEnrolments = (cohortCd = [], processType = [], from = '', to = '') => {
  return getEnrolmentCalendar(cohortCd, processType, from, to, true)
}

export const getStudentCalendarsByCourseId = courseCd =>
  get('/api/calendar/student', {
    params: {
      courseCd: courseCd
    }
  })

export const getAttendanceModeCodesDetails = () => {
  return cacheGet('ATTENDED_MODE_CODE', '/api/systemData/sone/attendanceModeCodes')
}

export const getAttendanceModeDescription = (attendanceModeList, unitattendanceModeCd) => {
    const attendanceModeCd = attendanceModeList.find(attendanceModeList => attendanceModeList.code === unitattendanceModeCd);
    if (attendanceModeCd) {
      return attendanceModeCd.description
    }
    return attendanceModeCd
  }

export const getEnrolmentCalendar = (cohortCd = [], processType = [], from = '', to = '', active = null) => {
  let params = {}

  if (processType && processType.length > 0) {
    params['processType'] = processType.join(',')
  }

  if (cohortCd && cohortCd.length > 0) {
    params['cohortCd'] = cohortCd.join(',')
  }

  if (active !== null) {
    params['active'] = active
  }

  if (from) {
    params['from'] = from
  }

  if (to) {
    params['to'] = to
  }

  return get(`/api/calendar/enrolment`, {
    params: params
  })
}

export const putStudentDeclaration = params => {
  return put('/api/student/declaration', params)
}

export const submitStudentDeclaration = courseId => {
  return getLatestEnrolmentCalendar(courseId).then(cohort => {
    const declaration = {
      enrCISeqNumber: cohort.cohortDtls.ciSequenceNo,
      enrCalType: cohort.cohortDtls.calType,
      courseCd: courseId
    }

    return putStudentDeclaration(declaration)
  })
}

export const getUsiDetails = () => {
  return get('/api/student/usiDetails')
}

export const putUsiDetails = params => {
  return put('/api/student/usiDetails', params)
}

export const putSoneFundingDetails = params => {
  return put('/api/student/fundingDetails', params)
}

export const getStudentProgress = courseId => {
  let studentProgress = JSON.parse(sessionStorage.getItem('STUDENT_PROGRESS_' + courseId))
  if (studentProgress === '') {
    sessionStorage.removeItem('STUDENT_PROGRESS_' + courseId)
  }

  return cacheGet('STUDENT_PROGRESS_' + courseId, '/api/student/progress', {
    params: {
      courseId: courseId
    }
  })
}

export const putStudentProgress = (courseId, studentId, currentPage, navSteps) => {
  return put('/api/student/progress', {
    courseId: courseId,
    currentPage: currentPage,
    navigationSteps: navSteps,
    studentId: studentId
  }).then(response => {
    if (sessionStorage.getItem('STUDENT_PROGRESS_' + courseId)) {
      let studentProgress = JSON.parse(sessionStorage.getItem('STUDENT_PROGRESS_' + courseId))
      if (studentProgress !== '') {
        studentProgress.currentPage = '' + currentPage
        studentProgress.navigationSteps = navSteps
        sessionStorage.setItem('STUDENT_PROGRESS_' + courseId, JSON.stringify(studentProgress))
      }

      return response
    }
  })
}

export const putUpdateCourseStatus = courseId => {
  return put('/api/student/updateCourseStatus?courseId=' + courseId, {})
}

export const getCoursePatterns = (courseId, academicYear, includePassedUnits = 'N') => {
  return get(`/api/courses/coursePatterns?courseId=${courseId}&academicYear=${academicYear}&includePassedUnits=${includePassedUnits}`)
}

export const getActiveMaintenancePeriods = () => {
  return get('/api/content/maintenancePeriods', {
    params: {
      activeOnly: true
    }
  }).then(response => response.data)
}

export const getStudyPeriodsCodesDescription = () => {
  return cacheGet('STUDY_PERIODS_DETAILS', '/api/systemData/sone/studyPeriods')
}

export const postAddressSearch = (countryCode, address) => {
  return postAsForm('/api/address/search', {
    params: {
      countryCode: countryCode,
      address: address
    }
  }).then(response => response)
}

export const postValidateAddress = (globalAddressKey, countryCode) => {
  return postAsForm(`/api/address/retrieve`, {
    params: {
      globalAddressKey: globalAddressKey,
      countryCode: countryCode
    }
  }).then(response => response)
}

function lessThanThirty(date) {
  //30 mins
  return Math.abs(getDifferenceInMinutes(date, new Date())) < 30
}